import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { useOrganizationId } from '@/contexts/OrganizationContext';
import type { SnackbarContextType } from '@/contexts/SnackbarContext.types';
import { createApiClient } from '@/shared/api/apiClient';
import type { components } from '@/shared/api/openapi';
import { handleSnackbarError } from '@/shared/utils/errorHandler';

export interface UserAllocationGroup {
  userId: string;
  allocationGroupId: number;
  createdUtc: string;
}

type AddUsersToAllocationGroupRequest = components['schemas']['AddUsersToAllocationGroupEndpoint_AddUsersRequest'];

export type AllocationSnapshot = components['schemas']['AddUsersToAllocationGroupEndpoint_AllocationGroupSnapshotRequest'];

interface AddUsersParams {
  allocationGroupId: number;
  userIds: string[];
  allocationGroup: AllocationSnapshot;
}

export interface RemoveUserParams {
  allocationGroupId: number;
  userId: string;
}

export interface RemoveUsersBatchParams {
  allocationGroupId: number;
  users: { userId: string }[];
}
export interface UseManageAllocationGroupUsersReturn {
  addUsers: (allocationGroupId: number, userIds: string[], allocationGroup: AllocationSnapshot) => Promise<UserAllocationGroup[] | null>;
  removeUser: (allocationGroupId: number, userId: string) => Promise<boolean>;
  removeUsers: (allocationGroupId: number, users: { userId: string }[]) => Promise<boolean>;
  isMutating: boolean;
  error: string | null;
  removeUserMutation: UseMutationResult<boolean, unknown, RemoveUserParams, unknown>;
  removeUsersBatchMutation: UseMutationResult<boolean, unknown, RemoveUsersBatchParams, unknown>;
}

export function useManageAllocationGroupUsers(snackbar: SnackbarContextType): UseManageAllocationGroupUsersReturn {
  const { organizationId } = useOrganizationId();
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();

  const addUsersMutation = useMutation({
    mutationFn: async (params: AddUsersParams): Promise<UserAllocationGroup[] | null> => {
      const requestBody: AddUsersToAllocationGroupRequest = {
        userIds: params.userIds,
        allocationGroup: params.allocationGroup,
      };

      const client = createApiClient(getAccessToken);

      const result = await client.POST('/api/organizations/{organizationId}/allocation-groups/{allocationGroupId}/users', {
        params: {
          path: { organizationId, allocationGroupId: params.allocationGroupId },
        },
        body: requestBody,
      });

      return result.data && Array.isArray(result.data.successes) ? (result.data.successes as UserAllocationGroup[]) : null;
    },
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: ['usersByAllocationGroup', organizationId], exact: false }, { cancelRefetch: false }),

        queryClient.invalidateQueries(
          {
            queryKey: ['users-autocomplete', organizationId],
            exact: false,
          },
          { cancelRefetch: false },
        ),
        queryClient.invalidateQueries(
          {
            queryKey: ['allocation-group-user-membership', organizationId],
            exact: false,
          },
          { cancelRefetch: false },
        ),
      ]);
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Failed to add users';
      handleSnackbarError(err, snackbar, message);
    },
  });

  const removeUserMutation = useMutation({
    mutationFn: async (params: RemoveUserParams): Promise<boolean> => {
      const client = createApiClient(getAccessToken);

      const result = await client.DELETE('/api/organizations/{organizationId}/allocation-groups/{allocationGroupId}/users', {
        params: {
          path: { organizationId, allocationGroupId: params.allocationGroupId },
          query: { UserId: params.userId },
        },
      });

      // DELETE endpoint returns 204 NoContent on success
      return result.response.status === 204 || result.response.ok;
    },
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: ['usersByAllocationGroup', organizationId] }, { cancelRefetch: false });
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Failed to remove user';
      handleSnackbarError(err, snackbar, message);
    },
  });

  const removeUsersBatchMutation = useMutation({
    mutationFn: async (params: RemoveUsersBatchParams) => {
      const client = createApiClient(getAccessToken);

      const result = await client.DELETE('/api/organizations/{organizationId}/allocation-groups/{allocationGroupId}/users/batch', {
        params: {
          path: { organizationId, allocationGroupId: params.allocationGroupId },
        },
        body: {
          users: params.users.map((user) => user.userId),
        },
      });

      return result.response.ok;
    },
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries(
          {
            queryKey: ['usersByAllocationGroup', organizationId],
          },
          { cancelRefetch: false },
        ),
        queryClient.invalidateQueries(
          {
            queryKey: ['allocationGroups', organizationId],
          },
          { cancelRefetch: false },
        ),
      ]);
    },
    onError: (err: unknown) => {
      handleSnackbarError(err, snackbar, 'Failed to remove users');
    },
  });

  const addUsers = async (
    allocationGroupId: number,
    userIds: string[],
    allocationGroup: AllocationSnapshot,
  ): Promise<UserAllocationGroup[] | null> => {
    try {
      return await addUsersMutation.mutateAsync({ allocationGroupId, userIds, allocationGroup });
    } catch {
      return null;
    }
  };

  const removeUser = async (allocationGroupId: number, userId: string): Promise<boolean> => {
    try {
      return await removeUserMutation.mutateAsync({ allocationGroupId, userId });
    } catch {
      return false;
    }
  };

  const removeUsers = async (allocationGroupId: number, users: { userId: string }[]): Promise<boolean> => {
    try {
      return await removeUsersBatchMutation.mutateAsync({ allocationGroupId, users });
    } catch {
      return false;
    }
  };

  // Determine the current operation type and overall loading state
  const isMutating = addUsersMutation.isPending || removeUserMutation.isPending || removeUsersBatchMutation.isPending;

  const error =
    addUsersMutation.error instanceof Error
      ? addUsersMutation.error.message
      : removeUserMutation.error instanceof Error
        ? removeUserMutation.error.message
        : removeUsersBatchMutation.error instanceof Error
          ? removeUsersBatchMutation.error.message
          : null;

  return {
    addUsers,
    removeUser,
    removeUsers,
    isMutating,
    error,
    removeUserMutation,
    removeUsersBatchMutation,
  };
}
